import { useState, useRef, useEffect, useCallback, useMemo, type FC } from 'react';
import { Search, Filter, Plus, Edit2, Trash2, ChevronLeft, ChevronRight, Download, EyeIcon, RefreshCcw, PencilIcon, User, Users, Calendar } from 'lucide-react';
import { fetchPutKaryawan, makeInitials, calculateAge, PhotoUploadField, fetchPostKaryawan, fetchGetKota, fetchDeleteKaryawan } from '../../../../../controller/dashboard/Umum/Personalia/DataKaryawan';
import { getThemeStyles, Theme } from '../../../../../util/style';
import { GlobalStyles } from '../../../../../assets/style/personalia/globalcss';
import { Employee2, EMPTY_KARYAWAN } from '../../../../../model/Employee';
import { FormField, StatusBadge } from '../../../../../controller/dashboard/Umum/Personalia/DataKaryawan';

// components
import EmployeeTable from './components/EmployeeTable';
import StatCard from './components/StatCard';

// hooks
import { useDeleteEmployee } from './hooks/useDeleteEmployee';
import EmployeeModalDetail from './components/EmployeeModalDetail';
import EmployeeModalForm from './components/EmployeeModalForm';
import { cekTipeDataJson } from '../../../../../controller';

type DetailTab = "umum" | "rekening" | "pendidikan" | "resign";
type FormTab = "umum" | "rekening" | "pendidikan" | "resign";
type ModalMode = "detail" | "add" | "edit" | "delete" | null;

// Type definitions
type Pendidikan = { jenjang: string; institusi: string; jurusan: string; tahun_lulus: number | null; bidang_keahlian: string };
type Resign = { tanggal_keluar: string; alasan: string; keterangan: string };
type Rekening = Record<string, string>;
type Kota = { id: number | string; nama_kota: string };
type Karyawan = Employee2;

const DetailModal: FC<{
  isDark?: boolean;
  karyawan: Employee2;
  onClose: () => void;
  onEdit: (k: Employee2) => void;
}> = ({ isDark = false, karyawan: e, onClose, onEdit })=> {
  const [tab, setTab] = useState<DetailTab>("umum");
  const [showPhotoPreview, setShowPhotoPreview] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (ev: MouseEvent) => {
      if (ref.current && !ref.current.contains(ev.target as Node)) onClose();
    };
    setTimeout(() => document.addEventListener("mousedown", handler), 0);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  return (
    <div className="modal-overlay">
      <div className="modal" ref={ref}>
        <div className="modal-header">
          <span className={`modal-title`} style={ isDark ? { color: '#e6edf3' } : { color: 'black' }}>Detail — {e.nama}</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {/* TABS */}
          <div className="modal-tabs">
              <button className={`modal-tab${tab === "umum" ? " active" : ""}`} onClick={() => setTab("umum")}>
                📋 Info Umum
              </button>
              <button className={`modal-tab${tab === "rekening" ? " active" : ""}`} onClick={() => setTab("rekening")}>
                🏦 Rekening JSON
              </button>
              <button className={`modal-tab${tab === "pendidikan" ? " active" : ""}`} onClick={() => setTab("pendidikan")}>
                🎓 Pendidikan JSON
              </button>
              <button className={`modal-tab${tab === "resign" ? " active" : ""}`} onClick={() => setTab("resign")}>
                🚪 Resign JSON
              </button>
          </div>

          {/* TAB: INFO UMUM */}
          {tab === "umum" && (
            <>
              {showPhotoPreview && e.foto_karyawan && (
                <div className="modal-overlay" onClick={() => setShowPhotoPreview(false)}>
                  <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "600px" }}>
                    <div className="modal-header">
                      <span className={`modal-title`} style={ isDark ? { color: '#e6edf3' } : { color: 'black' }}>Preview Foto — {e.nama}</span>
                      <button className="modal-close" onClick={() => setShowPhotoPreview(false)}>✕</button>
                    </div>
                    <div className="modal-body" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
                      <img 
                        src={process.env.REACT_APP_API_URL + "/storage/route/karyawan/" + encodeURIComponent(e.nama) + "/" + e.foto_karyawan} 
                        alt={e.nama} 
                        style={{ maxWidth: '100%', maxHeight: '500px', objectFit: 'contain' }}
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className="detail-section">
                <div className="detail-section-title">Data Pribadi</div>
                <div className="detail-grid">
                  {[
                    ["", e.foto_karyawan ? <img src={process.env.REACT_APP_API_URL + "/storage/route/karyawan/" + encodeURIComponent(e.nama) + "/" + e.foto_karyawan} alt={e.nama} className="w-16 h-16 object-cover rounded-full cursor-pointer hover:opacity-80 transition-opacity" onClick={() => setShowPhotoPreview(true)} style={{ cursor: 'pointer' }} /> : "—"], [],
                    ["NIK", e.nik], ["Nama Lengkap", e.nama],
                    ["Jenis Kelamin", e.jk],
                    ["Agama", e.agama],
                    ["Tgl Lahir", `${e.tgl_lahir_formatted} (${e.usia} th)`],
                    ["Email", e.email],
                    ["HP Pribadi", e.no_hp_pribadi || "—"],
                    ["HP Perusahaan", e.no_hp_perusahaan || "—"],
                    ["Alamat Rumah", e.alamat_rumah],
                    ["Kota", `${e.kota} (${e.kode_pos})`],
                    ["NPWP", e.npwp || "—"]
                  ].map(([k, v], idx) => (
                    <div key={idx} className="detail-item">
                      <div className="detail-key">{k}</div>
                      <div className="detail-val">{v}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="detail-section">
                <div className="detail-section-title">Data Pekerjaan</div>
                <div className="detail-grid">
                  {[
                    ["Jabatan", e.jabatan], ["Divisi", e.divisi],
                    ["Status", e.status_pegawai], ["Bergabung", e.tgl_bergabung_formatted],
                    ["Lokasi", e.lokasi_kerja], ["Lama Kerja", `${e.lama_kerja}`],
                  ].map(([k, v]) => (
                    <div key={k} className="detail-item">
                      <div className="detail-key">{k}</div>
                      <div className="detail-val">{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* TAB: REKENING JSON */}
          {tab === "rekening" && (
            <>
              <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 12 }}>
                Data rekening yang tersimpan untuk karyawan ini.
              </p>
              {Object.keys(e.rekening).length > 0 ? (
                <div className="detail-grid">
                  {Object.entries(e.rekening).map(([bank, nomor]) => (
                    <div key={bank} className="detail-item">
                      <div className="detail-key">{bank}</div>
                      <div className="detail-val">{String(nomor)}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="json-view-block">
                  <div className="json-view-row">
                    <span className="json-null">Belum ada rekening terdaftar.</span>
                  </div>
                </div>
              )}
            </>
          )}

          {/* TAB: PENDIDIKAN JSON */}
          {tab === "pendidikan" && (
            <>
              <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 12 }}>
                Detail pendidikan karyawan.
              </p>
              <div className="detail-grid">
                {Object.entries(e.pendidikan).map(([key, value]) => (
                  <div key={key} className="detail-item">
                    <div className="detail-key">{key.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase())}</div>
                    <div className="detail-val">{String(value ?? '—')}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* TAB: RESIGN JSON */}
          {tab === "resign" && (
            <>
              <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 12 }}>
                Status resign karyawan.
              </p>
              {e.resign && Object.keys(e.resign).length > 0 ? (
                <div className="detail-grid">
                  {Object.entries(e.resign ?? {}).map(([key, value]) => (
                    <div key={key} className="detail-item">
                      <div className="detail-key">{key.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase())}</div>
                      <div className="detail-val">{String(value ?? '—')}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="resign-null-msg">
                  Karyawan masih aktif bekerja. Data resign belum tersedia.
                </div>
              )}
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn" onClick={onClose}>Tutup</button>
          <button className="btn btn-primary" onClick={() => { onClose(); onEdit(e); }}>✏️ Edit Karyawan</button>
        </div>
      </div>
    </div>
  );
};

const FormModal: FC<{
  isDark?: boolean;
  mode: "add" | "edit";
  initial: Karyawan | null;
  onClose: () => void;
  onSave: (data: Omit<Karyawan, "id"> & { id?: number }) => void;
  onEdit: (data: Omit<Karyawan, "id"> & { id?: number }) => void;
  onGetKaryawan: () => any;
}> = ({ isDark, mode, initial, onClose, onSave, onEdit, onGetKaryawan }) => {
  const [processStatus, setProcessStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [tab, setTab] = useState<FormTab>("umum");
  const [resspp, setResspp] = useState(true);
  const [kota, setKota] = useState<Kota[]>([]);
  const [form, setForm] = useState<Omit<Karyawan, "id"> & { id?: number }>(
    initial
      ? { ...EMPTY_KARYAWAN, ...initial }
      : { ...EMPTY_KARYAWAN, id: undefined }
  );
  // Rekening as array of [bank, no] pairs for editing
  const [rekEntries, setRekEntries] = useState<[string, string][]>(
    Object.entries(initial?.rekening ?? {})
  );
  const [resignEnabled, setResignEnabled] = useState(!!initial?.resign);
 
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (ev: MouseEvent) => {
      if (ref.current && !ref.current.contains(ev.target as Node)) onClose();
    };
    setTimeout(() => document.addEventListener("mousedown", handler), 0);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);
 
  const set = useCallback(<K extends keyof typeof form>(key: K, val: typeof form[K]) => {
    setForm((prev) => ({ ...prev, [key]: val }));
  }, []);
 
  const setPend = useCallback(<K extends keyof Pendidikan>(key: K, val: Pendidikan[K]) => {
    setForm((prev) => ({ ...prev, pendidikan: { ...prev.pendidikan, [key]: val } }));
  }, []);
 
  const setRes = useCallback(<K extends keyof Resign>(key: K, val: string) => {
    setForm((prev) => ({
      ...prev,
      resign: { ...(prev.resign ?? { tanggal_keluar: "", alasan: "", keterangan: "" }), [key]: val },
    }));
  }, []);
 
  const addRekEntry = () => setRekEntries((p) => [...p, ["", ""]]);
  const updateRek = (i: number, col: 0 | 1, val: string) => {
    setRekEntries((p) => p.map((e, idx) => idx === i ? (col === 0 ? [val, e[1]] : [e[0], val]) : e));
  };
  const removeRek = (i: number) => setRekEntries((p) => p.filter((_, idx) => idx !== i));
 
  const rekeningObj = useMemo<Rekening>(() => {
    const obj: Rekening = {};
    rekEntries.forEach(([k, v]) => { if (k.trim()) obj[k.trim()] = v.trim(); });
    return obj;
  }, [rekEntries]);

  const checkSuccess = (result: any) => {
    return Boolean(
      result?.ok ||
      result?.status === 200 ||
      result?.status === 201 ||
      result?.status === "200" ||
      result?.status === "201" ||
      String(result?.status || "").startsWith("2") ||
      result?.status === "success"
    );
  };

  const handleEdit = async () => {
    const saved = { ...form, inisial: makeInitials(form.nama), rekening: rekeningObj, resign: resignEnabled ? form.resign : null, nama_old : initial?.nama, nik_old : initial?.nik };
    setProcessStatus("loading");
    try {
      const result : any = resspp ? await onEdit(saved) : { status: 500, message: "Simulasi error", console : saved };
      if (checkSuccess(result)) {
        setProcessStatus("success");
        setTimeout(() => onClose(), 1500);
      } else {
        console.error("Edit failed:", result);
        setProcessStatus("error");
        setTimeout(() => setProcessStatus("idle"), 1500);
      }
    } catch (error) {
      console.error(error);
      setProcessStatus("error");
    }
  };
 
  const handleSave = async () => {
    const saved = { ...form, inisial: makeInitials(form.nama), rekening: rekeningObj, resign: resignEnabled ? form.resign : null, nama_old : initial?.nama, nik_old : initial?.nik };
    setProcessStatus("loading");
    try {
      const result : any = resspp ? await onSave(saved) : { status: 500, message: "Simulasi error", console : saved };
      if (checkSuccess(result)) {
        setProcessStatus("success");
        setTimeout(() => onClose(), 1500);
      } else {
        console.error("Save failed:", result);
        setProcessStatus("error");
        setTimeout(() => setProcessStatus("idle"), 1500);
      }
    } catch (error) {
      console.error(error);
      setProcessStatus("error");
    }
    // console.log(saved);
  };

  const handleNext = () => {
    if (tab === "umum") setTab("rekening");
    else if (tab === "rekening") setTab("pendidikan");
    else if (tab === "pendidikan") setTab("resign");
  }

  const handlePrev = () => {
    if (tab === "resign") setTab("pendidikan");
    else if (tab === "pendidikan") setTab("rekening");
    else if (tab === "rekening") setTab("umum");
  }

  const isFormValid = form.nama.trim() !== "" && form.nik.trim() !== "" && form.tgl_bergabung.trim() !== "";

  const processingandsaved = () => {
    return(
      <div className="process-container">
      {processStatus === "loading" && (
        <div className="status-box">
          <div className="spinner"></div> {/* Tambahkan CSS spinner Anda */}
          <p>Sedang memproses data, mohon tunggu...</p>
        </div>
      )}

      {processStatus === "success" && (
        <div className="status-box success">
          <div className="icon">✅</div>
          <h3>Berhasil!</h3>
          <p>Data telah berhasil disimpan ke database.</p>
        </div>
      )}

      {processStatus === "error" && (
        <div className="status-box error">
          <div className="icon">❌</div>
          <h3>Terjadi Kesalahan</h3>
          <p>Gagal menyimpan data. Silakan coba lagi</p>
        </div>
      )}
    </div>
    )
  }

  useEffect(() => {
    onGetKaryawan()
      .then(setKota)
      .catch(() => setKota([]));
  }, [onGetKaryawan]);
  
  useEffect(() => {
    if (form.tgl_lahir) {
      const calculatedAge = calculateAge(form.tgl_lahir);
      if (calculatedAge !== form.usia) {
        set("usia", calculatedAge);
      }
    }
  }, [form.tgl_lahir]);
 
  const TABS: { key: FormTab; label: string }[] = [
    { key: "umum", label: "📋 Data Umum" },
    { key: "rekening", label: "🏦 Rekening JSON" },
    { key: "pendidikan", label: "🎓 Pendidikan JSON" },
    { key: "resign", label: "🚪 Resign JSON" },
  ];
 
  return (
    <div className="modal-overlay">
      <div className="modal bg-white" ref={ref}>
        <div className="modal-header">
          <span className="modal-title" style={ isDark  ? { color: "white" } : { color: "black" } }>{mode === "add" ? "Tambah Karyawan Baru" : `Edit — ${initial?.nama}`}</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {processStatus !== "idle" ? 
            processingandsaved() : 
            <>
              <div className="modal-tabs">
                {TABS.map((t) => (
                  <button key={t.key} className={`modal-tab${tab === t.key ? " active" : ""}`} onClick={() => setTab(t.key)}>
                    {t.label}
                  </button>
                ))}
              </div>
    
              {/* ── TAB: UMUM ── */}
              {tab === "umum" && (
                <div className="form-grid">
                  {/* <FormField label="Tgl Bergabung" full>
                    <input type="date" className="form-input" value={form.tgl_bergabung.split('T')[0]} onChange={(e) => set("tgl_bergabung", e.target.value)} disabled={mode === "edit"} />
                  </FormField> */}
                  <div className="form-section-head" onClick={() => console.log(form)}>Data Pribadi</div>
                  <FormField label="Foto Karyawan" full>
                    <PhotoUploadField
                      value={form.foto_karyawan}
                      onChange={(dataUrl) => set("foto_karyawan", dataUrl)}
                      isDark={isDark} employeeName={form.nama}
                    />
                  </FormField>
                  <FormField label="NIK"><input className="form-input" value={form.nik} onChange={(e) => set("nik", e.target.value)} placeholder="001251" /></FormField>
                  <FormField label="Nama Karyawan"><input className="form-input" value={form.nama} onChange={(e) => set("nama", e.target.value)} placeholder="Nama lengkap" /></FormField>
                  <FormField label="Jenis Kelamin"><select className="form-select" value={form.jk} onChange={(e) => set("jk", e.target.value)}><option value="LK">Laki-laki</option><option value="PM">Perempuan</option></select></FormField>
                  <FormField label="Agama"><select className="form-select" value={form.agama} onChange={(e) => set("agama", e.target.value)}>{["Islam","Kristen","Katolik","Hindu","Buddha","Konghucu"].map((v) => <option key={v} value={v}>
                    {v}
                  </option>)}</select></FormField>
                  <FormField label="Tanggal Lahir"><input type="date" className="form-input" value={form.tgl_lahir.split('T')[0]} onChange={(e) => set("tgl_lahir", e.target.value)} /></FormField>
                  <FormField label="Usia"><input type="number" className="form-input" value={form.usia || ""} onChange={(e) => set("usia", parseInt(e.target.value) || 0)} placeholder="25" disabled /></FormField>
                  <FormField label="No. HP Pribadi"><input className="form-input" value={form.no_hp_pribadi} onChange={(e) => set("no_hp_pribadi", e.target.value)} placeholder="0812xxx" /></FormField>
                  <FormField label="No. HP Perusahaan"><input className="form-input" value={form.no_hp_perusahaan ?? ""} onChange={(e) => set("no_hp_perusahaan", e.target.value || null)} placeholder="0811xxx" /></FormField>
                  <FormField label="Email" full><input type="email" className="form-input" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="nama@co.id" /></FormField>
                  <FormField label="Alamat Rumah" full><input className="form-input" value={form.alamat_rumah} onChange={(e) => set("alamat_rumah", e.target.value)} placeholder="Sesuai KTP" /></FormField>
                  <FormField label="Alamat Sekarang" full><input className="form-input" value={form.alamat_sekarang} onChange={(e) => set("alamat_sekarang", e.target.value)} placeholder="Domisili saat ini" /></FormField>
                  <FormField label="Kota"><select className="form-select" value={form.kota} onChange={(e) => { console.log(e); set("kota", e.target.value) }}> <option value="">Pilih Kota</option>{ kota.map((v) => <option key={v.id} value={v.id}>
                    {v.id} - {v.nama_kota}
                  </option>)}</select></FormField>
                  <FormField label="Kode Pos"><input className="form-input" value={form.kode_pos} onChange={(e) => set("kode_pos", e.target.value)} placeholder="12345" /></FormField>
                  <FormField label="NPWP"><input className="form-input" value={form.npwp ?? ""} onChange={(e) => set("npwp", e.target.value || null)} placeholder="XX.XXX.XXX.X-XXX" /></FormField>
    
                  <div className="form-section-head">Data Pekerjaan</div>
                  {/* <FormField label="Jabatan"><select className="form-select" value={form.jabatan} onChange={(e) => set("jabatan", e.target.value)}>{["Staff","Staff Senior","Supervisor","Manager","Direktur"].map((v) => <option key={v}>{v}</option>)}</select></FormField>
                  <FormField label="Divisi"><select className="form-select" value={form.divisi} onChange={(e) => set("divisi", e.target.value)}>{["Teknologi","Keuangan","Pemasaran","Operasional","SDM","Logistik"].map((v) => <option key={v}>{v}</option>)}</select></FormField> */}
                  <FormField label="Status Pegawai"><select className="form-select" value={form.status_pegawai} onChange={(e) => set("status_pegawai", e.target.value as Karyawan["status_pegawai"])}><option value="Tetap">Tetap</option><option value="Kontrak">Kontrak</option><option value="Magang">Magang</option></select></FormField>
                  <FormField label="Tgl Bergabung"><input type="date" className="form-input" value={form.tgl_bergabung.split('T')[0]} onChange={(e) => set("tgl_bergabung", e.target.value)} /></FormField>
                  <FormField label="Periode Kontrak"><input type="date" className="form-input" value={form.periode_kontrak?.split('T')[0] || ""} onChange={(e) => set("periode_kontrak", e.target.value || null)} /></FormField>
                  <FormField label="Lokasi Kerja"><input className="form-input" value={form.lokasi_kerja} onChange={(e) => set("lokasi_kerja", e.target.value)} placeholder="Gedung A, Lt.3" /></FormField>
                </div>
              )}
    
              {/* ── TAB: REKENING ── */}
              {tab === "rekening" && (
                <>
                  {rekEntries.map(([bank, no], i) => (
                    <div key={i} className="json-entry">
                      <input className="form-input" placeholder="Nama Bank (BNI, BCA...)" value={bank} onChange={(e) => updateRek(i, 0, e.target.value)} />
                      <input className="form-input" placeholder="Nomor Rekening" value={no} onChange={(e) => updateRek(i, 1, e.target.value)} />
                      <button className="json-rm-btn" onClick={() => removeRek(i)}>✕</button>
                    </div>
                  ))}
                  <button className="json-add-btn" onClick={addRekEntry}>➕ Tambah Rekening Bank</button>
                </>
              )}
    
              {/* ── TAB: PENDIDIKAN ── */}
              {tab === "pendidikan" && (
                <>
                  <div className="form-grid">
                    <FormField label="Jenjang">
                      <select className="form-select" value={form.pendidikan.jenjang} onChange={(e) => setPend("jenjang", e.target.value)}>
                        {["S3","S2","S1","D4","D3","D2","SMA/SMK"].map((v) => <option key={v}>{v}</option>)}
                      </select>
                    </FormField>
                    <FormField label="Nama Institusi"><input className="form-input" value={form.pendidikan.institusi} onChange={(e) => setPend("institusi", e.target.value)} placeholder="Universitas / Sekolah" /></FormField>
                    <FormField label="Jurusan / Prodi"><input className="form-input" value={form.pendidikan.jurusan} onChange={(e) => setPend("jurusan", e.target.value)} placeholder="Teknik Informatika" /></FormField>
                    <FormField label="Tahun Lulus"><input type="number" className="form-input" value={form.pendidikan.tahun_lulus ?? ""} onChange={(e) => setPend("tahun_lulus", parseInt(e.target.value) || null)} placeholder="2020" /></FormField>
                    <FormField label="Bidang Keahlian" full><input className="form-input" value={form.bidang_keahlian} onChange={(e) => set("bidang_keahlian", e.target.value)} placeholder="Backend Development, dll." /></FormField>
                  </div>
                </>
              )}
    
              {/* ── TAB: RESIGN ── */}
              {tab === "resign" && (
                <>
                  <button
                    className="resign-toggle-row"
                    onClick={() => setResignEnabled((p) => !p)}
                  >
                    <input type="checkbox" style={{ accentColor: "var(--accent3)", width: 15, height: 15 }} checked={resignEnabled} readOnly />
                    Aktifkan data resign
                  </button>
                  {resignEnabled ? (
                    <>
                      <div className="form-grid">
                        <FormField label="Tanggal Keluar"><input type="date" className="form-input" value={form.resign?.tanggal_keluar ?? ""} onChange={(e) => setRes("tanggal_keluar", e.target.value)} /></FormField>
                        <FormField label="Alasan">
                          <select className="form-select" value={form.resign?.alasan ?? ""} onChange={(e) => setRes("alasan", e.target.value)}>
                            {["Pindah domisili","Pekerjaan lain","Keluarga","Kesehatan","Pensiun","Kontrak berakhir","Lainnya"].map((v) => <option key={v}>{v}</option>)}
                          </select>
                        </FormField>
                        <FormField label="Keterangan" full><input className="form-input" value={form.resign?.keterangan ?? ""} onChange={(e) => setRes("keterangan", e.target.value)} placeholder="Catatan tambahan..." /></FormField>
                      </div>
                    </>
                  ) : (
                    <div className="resign-null-msg">
                      Kolom <code style={{ color: "var(--text-dim)" }}>resign</code> akan disimpan sebagai <code style={{ color: "var(--accent2)" }}>null</code>.
                    </div>
                  )}
                </>
              )}
            </>
          }
        </div>
        {
          processStatus === "idle" && (
            <div className="modal-footer">
              { tab === "umum" ? null : <button className="btn" onClick={handlePrev}>Sebelumnya</button> }
              {  tab === "resign" ? 
                      mode === "add" ?
                      <> <button className="btn btn-primary" onClick={handleSave}>💾 Simpan Karyawan</button></>
                      :
                      <><button className="btn btn-primary" onClick={handleEdit}><PencilIcon /> Edit</button></>
                : 
                ( <button className="btn btn-default" onClick={handleNext}>Berikutnya</button> ) 
              }
            </div>
          )
        }
      </div>
    </div>
  );
};

const DeleteModal: FC<{
  isDark?: boolean;
  karyawan: Employee2;
  onClose: () => void;
  onDelete: (nik: string, nama: string) => void;
}> = ({ isDark, karyawan, onClose, onDelete }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (ev: MouseEvent) => {
      if (ref.current && !ref.current.contains(ev.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
    };
  }, [onClose]);
  
  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(karyawan.nik, karyawan.nama);
      setTimeout(() => onClose(), 1000);
    }
    catch (err) {
      console.error('Error deleting employee:', err);
      setIsDeleting(false);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal" ref={ref}>
        <div className="modal-header">
          <span className="modal-title" style={ isDark ? { color: "white" } : { color: "black" } }>Konfirmasi Hapus</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <p>Apakah Anda yakin ingin menghapus karyawan <strong>{karyawan.nama}</strong> (NIK: {karyawan.nik})?</p>
          <p style={{ color: "var(--text-muted)", fontSize: 13 }}>Tindakan ini tidak dapat dibatalkan.</p>
        </div>
        <div className="modal-footer">
          <button className="btn" onClick={onClose} disabled={isDeleting}>Batal</button>
          <button className="btn btn-danger" onClick={handleDelete} disabled={isDeleting}>
            {isDeleting ? "Menghapus..." : "Hapus Karyawan"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PersonaliaDataKaryawan({ theme = 'light' }: { theme?: Theme }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterDivisi, setFilterDivisi] = useState('Semua');
  const [filterStatus, setFilterStatus] = useState('Semua');
  const [currentPage, setCurrentPage] = useState(1);
  const [employees, setEmployees] = useState<Employee2[]>([]);

  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [activeKaryawan, setActiveKaryawan] = useState<Employee2 | null>(null);

  const [itemsPerPage, setItemsPerPage] = useState(5);

  const s = getThemeStyles(theme);
  const isDark = theme === 'dark';

  const handleModalOpen = (mode: ModalMode, karyawan?: Employee2 | null) => {
    setActiveKaryawan(karyawan ?? null);
    return setModalMode(mode);
  }

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL_V2}/karyawan`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${process.env.REACT_APP_API_KEY}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }

      const { data } = await response.json();

      const newData = data.map((item : any) => {
        return {
          ...item,
          pendidikan : cekTipeDataJson(item.pendidikan),
          rekening : cekTipeDataJson(item.rekening),
          resign : cekTipeDataJson(item.resign)
        };
      })
      setEmployees(newData);
    } catch (error) {
      console.error("Failed to fetch employees:", error);
      setEmployees([]);
    }
  }, []);

  

  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage, searchTerm, filterDivisi, filterStatus]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const filteredEmployees = employees ? employees.filter(emp => {
    const name = (emp.nama ?? '').toString();
    const nik = (emp.nik ?? '').toString();
    const term = (searchTerm ?? '').toString();
    const matchSearch = name.toLowerCase().includes(term.toLowerCase()) || nik.includes(term);
    const matchDivisi = filterDivisi === 'Semua' || emp.divisi === filterDivisi;
    const matchStatus = filterStatus === 'Semua' || emp.status_pegawai === filterStatus;
    return matchSearch && matchDivisi && matchStatus;
  }) : [];

  // Pagination logic
  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedEmployees = filteredEmployees.slice(startIndex, endIndex);

  // Generate page numbers untuk display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 3;
    
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 2) pages.push('...');
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPage < totalPages - 1) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);

    try {
      await fetchEmployees();
    } finally {
      setIsRefreshing(false);
    }
  };

  const { isLoading, handleDelete } = useDeleteEmployee();
  const [ detailModal, setDetailModal ] = useState<{ open: boolean; employee: Employee2 | null }>({ open: false, employee: null });
  const [ formModal, setFormModal ] = useState<{ open: boolean; employee: Employee2 | null }>({ open: false, employee: null });

  return (
    <>  
      <GlobalStyles theme={theme} />

      <EmployeeModalDetail theme={theme} isOpen={detailModal.open} onClose={() => { setDetailModal({ open: false, employee: null }); handleRefresh(); }} employee={detailModal.employee} />

      <EmployeeModalForm theme={theme} isOpen={formModal.open} onClose={() => { setFormModal({ open: false, employee: null }); handleRefresh(); }} employee={formModal.employee} />

      {(modalMode === "add" || modalMode === "edit") && (
        <FormModal
          mode={modalMode}
          initial={modalMode === "edit" ? activeKaryawan : null}
          onClose={() => { setModalMode(null); setActiveKaryawan(null); fetchEmployees(); } }
          onSave={async (data) => { return await fetchPostKaryawan(data); }}
          onEdit={async (data) => { return await fetchPutKaryawan(data); }}
          onGetKaryawan={ async () => { return await fetchGetKota(); }}
        />
      )}

      <main className="min-h-screen p-6">
        <div className="max-w-8xl mx-auto">
          <div className="mb-8">
            <div className="flex items-center mb-4">
              <Users className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <h1 className="text-3xl font-bold">Pendataan Karyawan</h1>
                <p>Kelola data karyawan dan informasi kontrak</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              <StatCard
                  theme={theme}
                  title="Total Karyawan"
                  value={employees.length}
                  icon={<Users className="w-6 h-6" />}
                  color="blue"
              />

              <StatCard
                  theme={theme}
                  title="Karyawan Tetap"
                  value={employees.filter(e => e.status_pegawai === 'Tetap').length}
                  icon={<User className="w-6 h-6" />}
                  color="green"
              />

              <StatCard
                  theme={theme}
                  title="Karyawan Kontrak"
                  value={employees.filter(e => e.status_pegawai === 'Kontrak').length}
                  icon={<User className="w-6 h-6" />}
                  color="orange"
              />

              <StatCard
                  theme={theme}
                  title="Karyawan Magang"
                  value={employees.filter(e => e.status_pegawai === 'Magang').length}
                  icon={<User className="w-6 h-6" />}
                  color="purple"
              />
            </div>
          </div>

          <div className={`${s.card} rounded-xl shadow-lg p-6 mb-6`}>
            <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
              <div className="flex flex-row gap-4 justify-center">
                <div className="relative w-full">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Masukkan nama atau NIK"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={`${s.bg} border ${s.cardBorder} h-full xl:w-64 md:w-full pl-10 pr-4 py-2 rounded-lg transition-all`}
                  />
                </div>
                <div className="relative w-full">
                  <button 
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                    className={`${s.bg} border ${s.cardBorder} h-full w-auto flex items-center gap-2 px-3 py-2 rounded-lg transition-all
                      ${(isFilterOpen || filterDivisi !== 'Semua' || filterStatus !== 'Semua') && 'border-blue-600 text-blue-600'}`}
                  >
                    <Filter size={16} /> Filter
                  </button>

                  {isFilterOpen && (
                    <div className={`absolute top-full mt-2 left-0 w-60 ${s.bg} rounded-xl shadow-2xl z-50 p-4`}>
                      <div className="mb-4">
                        <label className={`block text-xs font-semibold ${s.textSecondary} mb-2 uppercase tracking-wider`}>Divisi</label>
                        <select 
                          value={filterDivisi}
                          onChange={(e) => setFilterDivisi(e.target.value)}
                          className={`w-full ${s.bg} border ${s.cardBorder} rounded-lg py-1.5 px-2 text-sm focus:outline-none focus:border-blue-600`}
                        >
                          <option value="Semua">Semua Divisi</option>
                          <option value="Teknologi">Teknologi</option>
                          <option value="Keuangan">Keuangan</option>
                          <option value="SDM">SDM</option>
                          <option value="Pemasaran">Pemasaran</option>
                        </select>
                      </div>

                      <div className="mb-4">
                        <label className={`block text-xs font-semibold ${s.textSecondary} mb-2 uppercase tracking-wider`}>Status</label>
                        <select 
                          value={filterStatus}
                          onChange={(e) => setFilterStatus(e.target.value)} 
                          className={`w-full ${s.bg} border ${s.cardBorder} rounded-lg py-1.5 px-2 text-sm focus:outline-none focus:border-blue-600`}
                        >
                          <option value="Semua">Semua Status</option>
                          <option value="Tetap">Tetap</option>
                          <option value="Kontrak">Kontrak</option>
                          <option value="Probation">Probation</option>
                        </select>
                      </div>

                      <button 
                        onClick={() => { setFilterDivisi('Semua'); setFilterStatus('Semua'); setCurrentPage(1); }} 
                        className="w-full text-sm text-[#ff6b6b] hover:bg-[#ff6b6b]/10 py-1.5 rounded-lg" 
                      >
                        Reset Filter
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-3">
                <label className="flex items-center gap-2 text-sm whitespace-nowrap">
                  <span>Data per halaman</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                    aria-label="Jumlah data per halaman"
                    className={`${s.bg} border ${s.cardBorder} rounded-lg px-3 py-2`}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </label>

                <button 
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className={`${s.bg} border ${s.cardBorder} h-full w-36 flex items-center gap-2 px-3 py-2 rounded-lg transition-all`}
                >
                  <RefreshCcw size={16} className={`${isRefreshing && "animate-spin"}`} /> 
                  {isRefreshing ? "Memuat..." : "Muat Ulang"}
                </button>

                <button className={`${s.bg} border ${s.cardBorder} h-full w-auto flex items-center gap-2 px-3 py-2 rounded-lg transition-all`}>
                  <Download size={16} /> Export
                </button>

                <button 
                  onClick={() => setFormModal({ open: true, employee: null })}
                  // onClick={() => handleModalOpen("add")}
                  className={`bg-blue-600 text-white font-semibold h-full w-auto flex items-center gap-2 px-3 py-2 rounded-lg transition-all`}
                >
                  <Plus size={16} /> Tambah Karyawan
                </button>
              </div>
            </div>
          </div>

          <EmployeeTable 
            theme={isDark ? 'dark' : 'light'}
            employees={paginatedEmployees}
            isLoading={isLoading}
            onView={(employee: Employee2) => setDetailModal({ open: true, employee: employee })}
            onEdit={(employee: Employee2) => setFormModal({ open: true, employee: employee })}
            // onEdit={(employee: Employee2) => handleModalOpen('edit', employee)}
            onDelete={(employee: Employee2) => handleDelete(employee, () => fetchEmployees())}
          />

          <div className="mt-6 flex items-center justify-between">
              <div className="text-sm">
                Menampilkan {filteredEmployees.length > 0 ? startIndex + 1 : 0}-{Math.min(endIndex, filteredEmployees.length)} dari total {filteredEmployees.length} karyawan
              </div>
              <div className="flex space-x-2">
                <button 
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  className={`px-3 py-2 text-sm ${s.card} border ${s.cardBorder} rounded-lg`}
                >
                  Previous
                </button>

                {getPageNumbers().map((page, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentPage(Number(page))}
                    className={`px-3 py-1 text-white rounded-lg ${currentPage === page ? "bg-blue-500" : "bg-gray-200"}`}
                  >
                    {page}
                  </button>
                ))} 

                <button 
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  className={`px-3 py-2 text-sm ${s.card} border ${s.cardBorder} rounded-lg`}
                >
                  Next
                </button>
              </div>
          </div>
        </div>
      </main>      
    </>
  );
};